/**
 * Client for the gamification surface — XP/level, achievements, and the
 * all-time leaderboard. Self-contained fetch (mirrors
 * `features/daily/lib/dailyChallenge.ts` / `shared/announcements.ts`):
 * snake_case→camelCase at the boundary, never leaked past this file.
 *
 * XP is monotonic + server-persisted; level + achievements are derived on
 * read. The web diffs `totalXp` across drill turns for the per-spot tick
 * and fires the unlock modal off `newlyUnlocked`.
 */

import { backendUrl } from "@/shared/config";
import { loadTokens } from "@/features/auth/lib/tokenStorage";

export interface Achievement {
  id: string;
  title: string;
  description: string;
  icon: string;
  earned: boolean;
}

export interface Gamification {
  level: number;
  totalXp: number;
  intoLevel: number;
  levelSpan: number;
  achievements: Achievement[];
  newlyUnlocked: string[];
}

export interface LeaderboardEntry {
  handle: string;
  level: number;
  totalXp: number;
  rank: number;
  isYou: boolean;
  /** License tier badge — "Free" | "Core" | "Plus" | "Unlimited". */
  tier: string;
}

export interface Leaderboard {
  entries: LeaderboardEntry[];
  you: LeaderboardEntry | null;
  totalPlayers: number;
}

interface RawAchievement {
  id: string;
  title: string;
  description: string;
  icon: string;
  earned: boolean;
}

interface RawGamification {
  level: number;
  total_xp: number;
  into_level: number;
  level_span: number;
  achievements: RawAchievement[];
  newly_unlocked: string[];
}

interface RawLeaderboardEntry {
  handle: string;
  level: number;
  total_xp: number;
  rank: number;
  is_you: boolean;
  tier: string;
}

interface RawLeaderboard {
  entries: RawLeaderboardEntry[];
  you: RawLeaderboardEntry | null;
  total_players: number;
}

/** Exported for tests. */
export function mapGamification(raw: RawGamification): Gamification {
  return {
    level: raw.level,
    totalXp: raw.total_xp,
    intoLevel: raw.into_level,
    levelSpan: raw.level_span,
    achievements: raw.achievements.map((a) => ({ ...a })),
    newlyUnlocked: raw.newly_unlocked,
  };
}

function mapEntry(raw: RawLeaderboardEntry): LeaderboardEntry {
  return {
    handle: raw.handle,
    level: raw.level,
    totalXp: raw.total_xp,
    rank: raw.rank,
    isYou: raw.is_you,
    tier: raw.tier,
  };
}

/** Exported for tests. */
export function mapLeaderboard(raw: RawLeaderboard): Leaderboard {
  return {
    entries: raw.entries.map(mapEntry),
    you: raw.you ? mapEntry(raw.you) : null,
    totalPlayers: raw.total_players,
  };
}

async function authedRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const tokens = loadTokens();
  if (!tokens) throw new Error("Not signed in");
  const res = await fetch(backendUrl(path), {
    ...init,
    headers: {
      ...(init.headers as Record<string, string> | undefined),
      Authorization: `Bearer ${tokens.accessToken}`,
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Gamification request failed (${res.status}): ${text}`);
  }
  return res.json() as Promise<T>;
}

export async function getGamification(): Promise<Gamification> {
  return mapGamification(await authedRequest<RawGamification>("/gamification"));
}

export async function getLeaderboard(): Promise<Leaderboard> {
  return mapLeaderboard(await authedRequest<RawLeaderboard>("/leaderboard"));
}

export async function markAchievementsSeen(ids: string[]): Promise<void> {
  await authedRequest("/gamification/achievements/seen", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ achievement_ids: ids }),
  });
}
